import React, { useState, useEffect } from 'react';
import ExchangeInput from '../../base/exchange-input';
import usePayoutStore from '../../../store/payout';
import { useRateStore } from '../../../store';
import BaseLabelText from '../../base/label-text';
import { formatAmount } from '../../../utils/amount.util';
import { getCurrencySign } from '../../../utils/string.util';
import InfoIcon from '../../../assets/svgs/info.svg';

interface CurrencyExchangeProps {
  onNegotiateRate: () => void;
}

const CurrencyExchange: React.FC<CurrencyExchangeProps> = ({
  onNegotiateRate,
}) => {
  const { setPayoutPayload, payoutPayload, setPayoutSummary, payoutSummary } =
    usePayoutStore();
  const { currencyPairRate, getCurrencyPairRate } = useRateStore();

  const [selectedFromCurrency, setSelectedFromCurrency] = useState('');
  const [selectedToCurrency, setSelectedToCurrency] = useState('');

  const handleFromCurrencyChange = (currency: string) => {
    setSelectedFromCurrency(currency);
  };

  const handleToCurrencyChange = (currency: string) => {
    setSelectedToCurrency(currency);
  };

  useEffect(() => {
    setPayoutPayload({
      ...payoutPayload,
      fromCurrencyCode: selectedFromCurrency,
      toCurrencyCode: selectedToCurrency,
    });
  }, [selectedFromCurrency, selectedToCurrency]);

  const currencyPairRateHandler = async () => {
    await getCurrencyPairRate(
      payoutPayload.fromCurrencyCode,
      payoutPayload.toCurrencyCode,
    );
  };

  const amountConversionHandler = (amount: string, type: string) => {
    if (!currencyPairRate) return;

    const numericAmount = Number(amount.replace(/,/g, ''));

    if (type === 'from') {
      const convertedAmount =
        payoutPayload.fromCurrencyCode === currencyPairRate.baseCurrency
          ? numericAmount * currencyPairRate.buyRate
          : numericAmount / currencyPairRate.buyRate;

      setPayoutPayload({
        ...payoutPayload,
        amount: numericAmount,
        toAmount: parseFloat(convertedAmount.toFixed(2)),
      });
    } else {
      const convertedAmount =
        payoutPayload.toCurrencyCode === currencyPairRate.baseCurrency
          ? numericAmount * currencyPairRate.buyRate
          : numericAmount / currencyPairRate.buyRate;

      setPayoutPayload({
        ...payoutPayload,
        amount: parseFloat(convertedAmount.toFixed(2)),
        toAmount: numericAmount,
      });
    }
  };

  useEffect(() => {
    if (payoutPayload.fromCurrencyCode && payoutPayload.toCurrencyCode) {
      currencyPairRateHandler();
    }
  }, [payoutPayload.fromCurrencyCode, payoutPayload.toCurrencyCode]);

  useEffect(() => {
    if (currencyPairRate) {
      setPayoutSummary({
        exchangeRateText: `${getCurrencySign(currencyPairRate.baseCurrency)} 1 = ${getCurrencySign(currencyPairRate.pairedCurrency)} ${currencyPairRate.buyRate}`,
        sendingAmountText: `${getCurrencySign(payoutPayload.fromCurrencyCode)} ${formatAmount(payoutPayload.amount)}`,
        receivingAmountText: `${getCurrencySign(payoutPayload.toCurrencyCode)} ${formatAmount(payoutPayload.toAmount)}`,
      });
    }
  }, [currencyPairRate, payoutPayload.amount, payoutPayload.toAmount]);

  return (
    <div className="flex flex-col gap-6">
      <p className="text-gray-text text-sm">Currency Exchange</p>

      <div className="flex flex-col gap-8 items-center rounded-xl border border-[#F1F2F4] p-6">
        <div className="w-full grid grid-cols-2  gap-4 ">
          <ExchangeInput
            id="from-input"
            exchangeType="source"
            label="From"
            currencyValue={payoutPayload.fromCurrencyCode}
            required
            value={payoutPayload.amount.toString()}
            onChange={(amount) => {
              amountConversionHandler(amount, 'from');
            }}
            onCurrencyChange={handleFromCurrencyChange}
          />
          <ExchangeInput
            id="to-input"
            exchangeType="destination"
            label="To"
            currencyValue={payoutPayload.toCurrencyCode}
            required
            value={payoutPayload.toAmount.toString()}
            onChange={(amount) => {
              amountConversionHandler(amount, 'to');
            }}
            onCurrencyChange={handleToCurrencyChange}
          />
        </div>

        <div className="bg-[#F8F8F8] border-dashed border border-[#CBD5E0] rounded-xl p-2 w-full flex flex-col gap-4">
          {currencyPairRate ? (
            <div className="grid gap-2">
              <BaseLabelText
                label="Exchange Rate"
                value={payoutSummary.exchangeRateText}
              />
              <BaseLabelText
                label="You Send"
                value={payoutSummary.sendingAmountText}
              />
              <BaseLabelText
                label="Recipient Gets"
                value={payoutSummary.receivingAmountText}
              />
            </div>
          ) : (
            <div className="text-center p-2 flex flex-col gap-6 w-2/3 mx-auto">
              <img
                src={InfoIcon}
                alt="No Exchange Rate"
                className="w-10 h-10 mx-auto"
              />
              <p className="font-semibold text-xl">Exchange Rate Unavailable</p>
              <p className='font-normal'>
                We couldn't find an exchange rate for 
                {payoutPayload.fromCurrencyCode} to 
                {payoutPayload.toCurrencyCode}. Please try another currency pair
                or contact support for assistance.
              </p>
            </div>
          )}

          {/* <AppButton
            label="Negotiate Better Rate"
            type="dark"
            clicked={onNegotiateRate}
          /> */}
        </div>
      </div>
    </div>
  );
};

export default CurrencyExchange;
